import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faKey,
  faPlus,
  faSearch,
  faTrashAlt,
  faTimes,
  faExclamationTriangle,
  faShieldAlt,
  faTruck,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { UnitModel } from "../../model/UnitsModel";
import "./SettingsStyle.css";
import ChangePassword from "../ChangePassword/ChangePasswordPage";
import PayPage from "../PayPage/PayPage"; // <-- Neu importiert

interface State {
  token: string;
  accountStatus: string;
  testAlarms: number;
  isExpired: boolean;
  showPayModal: boolean; // <-- Neu
}

interface Actions {
  onAboExtendClick: () => void;
  closePayModal: () => void; // <-- Neu
  onBackClick: () => void;
  onDeleteAccountClick: () => Promise<void>;
}

interface UnitsState {
  searchTerm: string;
  filteredCars: UnitModel[];
}

interface UnitsActions {
  setSearchTerm: (term: string) => void;
  updateLocalName: (id: number, field: string, value: string) => void;
  handleNameChange: (unitID: string, value: string) => void;
  deleteCar: (id: number) => void;
  addCar: () => void;
}

interface Props {
  tag: string;
  state: State;
  actions: Actions;
  unitsState: UnitsState;
  unitsActions: UnitsActions;
  loggedState: number;
  isModal?: boolean;
}

const SettingsView: React.FC<Props> = ({
  tag,
  state,
  actions,
  unitsState,
  unitsActions,
  loggedState,
  isModal = false,
}) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return "Laden...";
    if (dateString === "Payed") return "Unbegrenzt";
    if (dateString === "Test") return "Testzugang";

    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2].substring(0, 2);
      return `${day}.${month}.${year}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    return dateString;
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    await actions.onDeleteAccountClick();
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  return (
    <div className="ja-settings-pageWrapper" style={{ visibility: loggedState === 0 ? "hidden" : "visible" }}>
      <div className="ja-settings-mainContainer">
        
        {/* Header */}
        <div className="ja-settings-header">
          {!isModal && (
            <button className="ja-settings-backButton" onClick={actions.onBackClick}>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Zurück</span>
            </button>
          )}
          <h2>Einstellungen</h2>
        </div>

        <div className="ja-settings-grid">
          
          {/* Karte 1: Account & Lizenz */}
          <div className="ja-settings-card">
            <div className="ja-settings-cardHeader">
              <FontAwesomeIcon icon={faShieldAlt} className="ja-settings-cardIcon" />
              <h3>Account & Lizenz</h3>
            </div>

            <div className="ja-settings-infoGroup">
              <label className="ja-login-label">App-Token:</label>
              <div className="ja-settings-tokenBox">
                <span>{state.token || "Kein Token vorhanden"}</span>
              </div>
            </div>

            <div className="ja-settings-infoGroup">
              <label className="ja-login-label">Abonnement-Status:</label>
              <div className="ja-settings-statusBadge">
                {state.accountStatus === "Test" ? (
                  <span>Mögliche Testalarme: <strong>{state.testAlarms}</strong></span>
                ) : (
                  <span>
                    Abo gültig bis:{" "}
                    <strong>{formatDate(state.accountStatus)}</strong>
                  </span>
                )}
              </div>
            </div>

            {state.accountStatus !== "Payed" && state.isExpired && (
              <div className="ja-settings-warningBox">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>Achtung: Das Abo ist abgelaufen oder läuft bald ab!</span>
                <button
                  className="ja-login-primaryButton ja-settings-renewBtn"
                  onClick={actions.onAboExtendClick}
                >
                  Abo jetzt verlängern
                </button>
              </div>
            )}

            <div className="ja-settings-actionRow">
              <button
                className="ja-settings-secondaryButton"
                onClick={() => setShowPasswordModal(true)}
              >
                <FontAwesomeIcon icon={faKey} />
                Passwort ändern
              </button>

              <button
                className="ja-settings-dangerButton"
                onClick={() => setShowDeleteModal(true)}
              >
                <FontAwesomeIcon icon={faUserSlash} />
                Account löschen
              </button>
            </div>
          </div>

          {/* Karte 2: Einheiten-Verwaltung */}
          <div className="ja-settings-card">
            <div className="ja-settings-cardHeader ja-settings-headerBetween">
              <div className="ja-settings-cardTitleLeft">
                <FontAwesomeIcon icon={faTruck} className="ja-settings-cardIcon" />
                <h3>Einheiten-Verwaltung</h3>
              </div>
              <button className="ja-settings-addButton" onClick={unitsActions.addCar}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Neu</span>
              </button>
            </div>

            {/* Suche */}
            <div className="ja-settings-searchContainer">
              <FontAwesomeIcon icon={faSearch} className="ja-settings-searchIcon" />
              <input
                type="text"
                placeholder="Einheit suchen..."
                className="ja-login-inputBox ja-settings-searchInput"
                value={unitsState.searchTerm}
                onChange={(e) => unitsActions.setSearchTerm(e.target.value)}
              />
            </div>

            {/* Tabelle */}
            <div className="ja-settings-tableWrapper">
              <table className="ja-settings-table">
                <thead>
                  <tr>
                    <th>Einheit Bezeichnung</th>
                    <th className="ja-settings-colAction">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {unitsState.filteredCars && unitsState.filteredCars.length > 0 ? (
                    unitsState.filteredCars.map((row) => (
                      <tr key={row.ID}>
                        <td>
                          <input
                            type="text"
                            className="ja-settings-tableInput"
                            value={row.Unit}
                            onChange={(e) =>
                              unitsActions.updateLocalName(row.ID, "Unit", e.target.value)
                            }
                            onBlur={(e) =>
                              unitsActions.handleNameChange(String(row.ID), String(e.target.value))
                            }
                          />
                        </td>
                        <td className="ja-settings-colAction">
                          <button
                            className="ja-settings-deleteBtn"
                            onClick={() => unitsActions.deleteCar(row.ID)}
                            title="Einheit löschen"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="ja-settings-noData">
                        Keine Einheiten vorhanden.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* Pop-Up Modal für PayPage / Abo verlängern */}
      {state.showPayModal && (
        <div className="ja-modal-overlay" onClick={actions.closePayModal}>
          <div className="ja-modal-card ja-modal-payCard" onClick={(e) => e.stopPropagation()}>
            <button className="ja-modal-close" onClick={actions.closePayModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <PayPage tag={tag} />
          </div>
        </div>
      )}

      {/* Pop-Up Modal für Passwort ändern */}
      {showPasswordModal && (
        <div className="ja-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="ja-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="ja-modal-close" onClick={() => setShowPasswordModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ChangePassword
              tag={tag} 
              onCloseModal={() => setShowPasswordModal(false)} 
            />
          </div>
        </div>
      )}

      {/* Pop-Up Modal für Account löschen */}
      {showDeleteModal && (
        <div className="ja-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="ja-modal-card ja-modal-deleteCard" onClick={(e) => e.stopPropagation()}>
            <button className="ja-modal-close" onClick={() => setShowDeleteModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div className="ja-settings-deleteDialog">
              <div className="ja-settings-deleteIconWrapper">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3>Account wirklich löschen?</h3>
              <p>
                Möchten Sie Ihren Account unwiderruflich löschen? Alle verknüpften Daten und Einheiten gehen dabei verloren.
              </p>

              <div className="ja-settings-deleteModalActions">
                <button
                  className="ja-settings-secondaryButton"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Abbrechen
                </button>
                <button
                  className="ja-login-primaryButton ja-settings-confirmDeleteBtn"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Wird gelöscht..." : "Ja, Account löschen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsView;